// Controller Sizing Calculator
// Per NEC 430.83

import { NECReference } from '../types';

export interface ControllerInput {
  motorHP: number;            // Motor horsepower
  motorFLA: number;           // Motor full-load amps (from NEC 430.248/430.250)
  voltage?: number;           // System voltage
  isTorqueMotor?: boolean;    // True if torque motor per NEC 430.83(D)
  isStationary?: boolean;     // True if stationary motor
}

export interface ControllerResult {
  minHPRating: number;        // Minimum HP rating per NEC 430.83(A)
  minAmpRating: number;       // Minimum continuous-duty current rating
  allowsGeneralPurpose: boolean;  // True if general-purpose controller allowed
  allowsSnapSwitch: boolean;  // True if snap switch allowed per NEC 430.83(C)
  nemaStarterSize: string;    // Recommended NEMA starter size (e.g., "00", "0", "1")
  breakdown: {
    motorHP: number;
    motorFLA: number;
    multiplier: number;
  };
  references: NECReference[];
}

// NEMA Motor Starter Sizes
// Based on NEMA ICS 2-2000
export interface NEMAStarterSize {
  size: string;  // String to support "00" size
  maxHP208V3Ph: number;
  maxHP230V3Ph: number;
  maxHP460V3Ph: number;
  maxHP575V3Ph: number;
  maxFLA: number;
}

// NEMA Starter Size Table (Three-Phase)
export const NEMA_STARTER_SIZES: NEMAStarterSize[] = [
  { size: '00', maxHP208V3Ph: 1.5, maxHP230V3Ph: 1.5, maxHP460V3Ph: 2, maxHP575V3Ph: 2, maxFLA: 9 },
  { size: '0', maxHP208V3Ph: 3, maxHP230V3Ph: 3, maxHP460V3Ph: 5, maxHP575V3Ph: 5, maxFLA: 18 },
  { size: '1', maxHP208V3Ph: 7.5, maxHP230V3Ph: 7.5, maxHP460V3Ph: 10, maxHP575V3Ph: 10, maxFLA: 27 },
  { size: '2', maxHP208V3Ph: 10, maxHP230V3Ph: 15, maxHP460V3Ph: 25, maxHP575V3Ph: 25, maxFLA: 45 },
  { size: '3', maxHP208V3Ph: 25, maxHP230V3Ph: 30, maxHP460V3Ph: 50, maxHP575V3Ph: 50, maxFLA: 90 },
  { size: '4', maxHP208V3Ph: 40, maxHP230V3Ph: 50, maxHP460V3Ph: 100, maxHP575V3Ph: 100, maxFLA: 135 },
  { size: '5', maxHP208V3Ph: 75, maxHP230V3Ph: 100, maxHP460V3Ph: 200, maxHP575V3Ph: 200, maxFLA: 270 },
  { size: '6', maxHP208V3Ph: 150, maxHP230V3Ph: 200, maxHP460V3Ph: 400, maxHP575V3Ph: 400, maxFLA: 540 },
  { size: '7', maxHP208V3Ph: 300, maxHP230V3Ph: 300, maxHP460V3Ph: 600, maxHP575V3Ph: 600, maxFLA: 810 },
  { size: '8', maxHP208V3Ph: 450, maxHP230V3Ph: 450, maxHP460V3Ph: 900, maxHP575V3Ph: 900, maxFLA: 1215 },
  { size: '9', maxHP208V3Ph: 800, maxHP230V3Ph: 800, maxHP460V3Ph: 1600, maxHP575V3Ph: 1600, maxFLA: 2250 },
];

/**
 * Get NEMA starter size for given HP and voltage
 */
export function getNEMAStarterSize(hp: number, voltage: number = 460): string {
  const voltageKey = voltage <= 208 ? 'maxHP208V3Ph'
    : voltage <= 230 ? 'maxHP230V3Ph'
    : voltage <= 460 ? 'maxHP460V3Ph'
    : 'maxHP575V3Ph';

  for (const starter of NEMA_STARTER_SIZES) {
    if ((starter[voltageKey as keyof NEMAStarterSize] as number) >= hp) {
      return starter.size;
    }
  }
  return NEMA_STARTER_SIZES[NEMA_STARTER_SIZES.length - 1].size;
}

/**
 * Get NEMA starter size by FLA
 */
export function getNEMAStarterSizeByFLA(fla: number): string {
  for (const starter of NEMA_STARTER_SIZES) {
    if (starter.maxFLA >= fla) {
      return starter.size;
    }
  }
  return NEMA_STARTER_SIZES[NEMA_STARTER_SIZES.length - 1].size;
}

/**
 * Calculate controller sizing per NEC 430.83
 *
 * Per NEC 430.83(A): Controller must have HP rating not less than motor HP
 * Per NEC 430.83(B): General-purpose allowed for 1/3 HP or less, 6A or less
 * Per NEC 430.83(C): Snap switch allowed for stationary 2 HP or less at 300V or less
 * Per NEC 430.83(D): Torque motors require a continuous-duty full-load current
 * rating not less than the nameplate current
 *
 * @param input - Controller sizing inputs
 * @returns ControllerResult with sizing information
 */
export function calculateControllerSize(input: ControllerInput): ControllerResult {
  const {
    motorHP,
    motorFLA,
    voltage = 480,
    isTorqueMotor = false,
    isStationary = false,
  } = input;

  const references: NECReference[] = [
    NEC_430_83_REF,
  ];

  // Per NEC 430.83(A): HP rating must be at least motor HP
  const minHPRating = motorHP;

  // Per NEC 430.83(D): a torque motor controller needs a continuous-duty
  // full-load current rating not less than the motor nameplate current (the
  // 115% figure belongs to the disconnect, 430.110(A), not the controller).
  const multiplier = 1.0;
  const minAmpRating = Math.ceil(motorFLA * multiplier);

  // Per NEC 430.83(B): General-purpose allowed for small motors
  // 1/3 HP or less with 6A or less FLA
  const allowsGeneralPurpose = motorHP <= 0.333 && motorFLA <= 6;

  // Per NEC 430.83(C): Snap switch allowed for stationary small motors
  // Stationary, 2 HP or less, 300V or less
  const allowsSnapSwitch = isStationary && motorHP <= 2 && voltage <= 300;

  // Get recommended NEMA starter size
  const nemaStarterSize = getNEMAStarterSize(motorHP, voltage);

  if (isTorqueMotor) {
    references.push(NEC_430_83_D_REF);
  }
  if (allowsGeneralPurpose) {
    references.push(NEC_430_83_B_REF);
  }
  if (allowsSnapSwitch) {
    references.push(NEC_430_83_C_REF);
  }

  return {
    minHPRating,
    minAmpRating,
    allowsGeneralPurpose,
    allowsSnapSwitch,
    nemaStarterSize,
    breakdown: {
      motorHP,
      motorFLA,
      multiplier,
    },
    references,
  };
}

// NEC References
export const NEC_430_83_REF: NECReference = {
  table: 'NEC 430.83',
  article: '430.83',
  description: 'Motor Controller Rating',
};

export const NEC_430_83_B_REF: NECReference = {
  table: 'NEC 430.83(B)',
  article: '430.83(B)',
  description: 'Small Motors - General-purpose controller for 1/3 HP or less',
};

export const NEC_430_83_C_REF: NECReference = {
  table: 'NEC 430.83(C)',
  article: '430.83(C)',
  description: 'Stationary Motors 2 HP or Less - Snap switch permitted',
};

export const NEC_430_83_D_REF: NECReference = {
  table: 'NEC 430.83(D)',
  article: '430.83(D)',
  description: 'Torque Motors - continuous-duty full-load current rating not less than the nameplate current',
};
